// @ts-nocheck
import {
  LeafletMap, TileLayer, GeoJSON as LeafletGeoJSON,
  Marker, CircleMarker, Icon, Control, DomUtil, DomEvent, LatLngBounds,
} from 'leaflet';
import { fetchPublicLayersBatch, PUBLIC_LAYER_NAMES } from '@fujigravel/api-contract';

/**
 * <map-component> — Generic Leaflet map custom element.
 *
 * Fetches each layer's GeoJSON FeatureCollection directly from the fujigravel
 * admin API (public, no-auth endpoint) and renders with Leaflet. Postgres +
 * PostGIS serve WGS84 GeoJSON natively — no gpkg, no sql.js, no client-side
 * WKB parser, no CRS reprojection.
 *
 * Attributes:
 *   event-id  {string}  Event slug (e.g. "2026-spring"), identifies which rows to fetch.
 *   api-base  {string}  Public API root (e.g. "https://admin.fujigravel.com/api/public").
 *                       Per-layer URL: `${api-base}/events/${event-id}/layers/${layer-name}`.
 *   layers    {string}  JSON array of layer config objects (HTML-escaped).
 *   bbox      {string}  Bounding box as "minLng,minLat,maxLng,maxLat".
 *   height    {number}  Map height in px (also settable via --map-height CSS var).
 *
 * Layer config object:
 *   {
 *     name:        string   — Display name (also used as layer name on the API path)
 *     tableName:   string?  — Override when layer name differs from display name
 *     color:       string   — CSS color for stroke and fill
 *     fillOpacity: number   — Fill opacity 0–1 (default 0.25)
 *     labelField:  string   — Feature property used as popup title (default "label")
 *     dashArray:   string?  — SVG dash pattern (e.g. "8,6") for dashed lines
 *     group:       string?  — Legend group name; layers sharing a group are grouped together
 *     offset:      number?  — Lateral pixel offset for polylines (requires leaflet.polylineoffset.js)
 *   }
 */

/**
 * Extract the first leaf [x, y] coordinate from a potentially nested array.
 * Used only by the post-fetch validity check.
 */
function _firstCoord(coords) {
  let c = coords;
  while (Array.isArray(c) && Array.isArray(c[0])) c = c[0];
  return c;
}

// ---------------------------------------------------------------------------
// Icon helpers
// ---------------------------------------------------------------------------

/**
 * Load an image and return [width, height] scaled to fit within maxSize
 * while preserving the natural aspect ratio.
 * Falls back to [maxSize, maxSize] on error.
 *
 * @param {string} url
 * @param {number} maxSize
 * @returns {Promise<[number, number]>}
 */
function loadIconSize(url, maxSize) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const { naturalWidth: w, naturalHeight: h } = img;
      if (w >= h) {
        resolve([maxSize, Math.round(maxSize * h / w)]);
      } else {
        resolve([Math.round(maxSize * w / h), maxSize]);
      }
    };
    img.onerror = () => resolve([maxSize, maxSize]);
    img.src = url;
  });
}

// ---------------------------------------------------------------------------
// Popup value formatting
// ---------------------------------------------------------------------------

function formatValue(value) {
  if (value === 0 || value === 1) {
    return value === 1
      ? '<span style="color:#16a34a;font-weight:700;">✓</span>'
      : '<span style="color:#dc2626;font-weight:700;">✗</span>';
  }
  const str = String(value);
  if (/^https?:\/\//.test(str)) {
    const escaped = str.replace(/"/g, '&quot;');
    return `<a href="${escaped}" target="_blank" rel="noopener" style="color:#3b82f6;text-decoration:underline;">${str}</a>`;
  }
  return str;
}

function escAttr(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escText(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Custom popup for the booth_spots layer. Mirrors the booth-listing card: logo
// (themed background), exhibitor name, surface-size badge, 物販/試乗車 badges.
// The whole card is wrapped in an <a> linking to /pages/booth?handle=<slug> —
// except for organizer-type booths which exist on the venue map but have no
// public detail page. No long description here (intentionally — that's on the
// detail page).
function buildBoothSpotPopup(p) {
  const tier = p.sponsor_tier || '';
  let surface = '';
  if (tier === 'platinum' || tier === 'gold')      surface = '3m×6m';
  else if (tier === 'silver' || tier === 'garage') surface = '3m×3m';

  const name = p.listed_name || p.exhibitor_name || '';
  const isOrganizer = p.booth_type === 'organizer';

  let logoHtml = '';
  if (p.logo_url) {
    const themeBg = p.logo_theme === 'light' ? '#e8e8e8' : p.logo_theme === 'dark' ? '#333' : 'rgba(0,0,0,0.04)';
    logoHtml =
      `<div style="background:${themeBg};display:flex;align-items:center;justify-content:center;height:64px;padding:6px;margin-bottom:6px;">` +
        `<img src="${escAttr(p.logo_url)}" alt="${escAttr(name)}" loading="lazy" style="max-width:140px;max-height:52px;object-fit:contain;">` +
      `</div>`;
  }

  const badges = [];
  if (tier === 'food') {
    badges.push('<span style="background:#d84315;color:#fff;padding:2px 6px;border-radius:0;font-size:0.7rem;font-weight:600;">飲食・体験</span>');
  } else if (surface) {
    badges.push(`<span style="background:#455a64;color:#fff;padding:2px 6px;border-radius:0;font-size:0.7rem;font-weight:600;">${surface}</span>`);
  }
  if (p.has_retail) {
    badges.push('<span style="background:#2e7d32;color:#fff;padding:2px 6px;border-radius:0;font-size:0.7rem;font-weight:600;">物販</span>');
  }
  if (p.has_test_bikes) {
    badges.push('<span style="background:#1565c0;color:#fff;padding:2px 6px;border-radius:0;font-size:0.7rem;font-weight:600;">試乗車</span>');
  }
  const badgesHtml = badges.length
    ? `<div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:4px;">${badges.join('')}</div>`
    : '';

  const inner =
    logoHtml +
    `<strong style="display:block;font-size:0.95rem;line-height:1.25;">${escText(name)}</strong>` +
    badgesHtml;

  if (isOrganizer || !p.handle) {
    return `<div style="min-width:160px;max-width:200px;color:inherit;">${inner}</div>`;
  }
  const href = `/pages/booth?handle=${encodeURIComponent(p.handle)}`;
  return `<a href="${href}" style="display:block;min-width:160px;max-width:200px;color:inherit;text-decoration:none;">${inner}</a>`;
}

// ---------------------------------------------------------------------------
// Permanent label styles — injected once per page
// ---------------------------------------------------------------------------

(function injectLabelStyles() {
  if (document.getElementById('map-component-label-styles')) return;
  const style = document.createElement('style');
  style.id = 'map-component-label-styles';
  style.textContent = `
    .map-component-label {
      background: rgba(255, 255, 255, 0.85);
      border: 1px solid rgba(0, 0, 0, 0.15);
      border-radius: 3px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
      color: #222;
      font-size: 11px;
      font-weight: 600;
      padding: 2px 6px;
      pointer-events: none;
      white-space: nowrap;
    }
    .map-component-label::before { display: none; }

    .map-component-legend {
      background: rgba(255, 255, 255, 0.92);
      border-radius: 6px;
      padding: 8px 10px;
      font-size: 12px;
      line-height: 1.6;
      overflow-y: auto;
      box-shadow: 0 1px 4px rgba(0,0,0,0.2);
    }
    .map-legend-row {
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      white-space: nowrap;
    }
    .map-legend-row input[type="checkbox"] {
      margin: 0;
      flex-shrink: 0;
    }
    .map-legend-swatch {
      display: inline-block;
      width: 14px;
      height: 14px;
      border-radius: 3px;
      flex-shrink: 0;
    }
    .map-legend-group {
      margin-bottom: 4px;
    }
    .map-legend-group-header {
      display: flex;
      align-items: center;
      gap: 4px;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
    }
    .map-legend-group-header input[type="checkbox"] {
      margin: 0;
    }
    .map-legend-group .map-legend-row {
      padding-left: 14px;
    }
  `;
  document.head.appendChild(style);
})();

// ---------------------------------------------------------------------------
// Web component
// ---------------------------------------------------------------------------

class MapComponent extends HTMLElement {
  #map = null;
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#init();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#map?.remove();
    this.#map = null;
  }

  async #init() {
    const eventId  = (this.getAttribute('event-id') || '').trim();
    const apiBase  = (this.getAttribute('api-base') || '').trim();
    const bboxAttr = this.getAttribute('bbox') || '';

    let layers = [];
    try {
      layers = JSON.parse(this.getAttribute('layers') || '[]');
    } catch (err) {
      console.error('[map-component] Invalid layers JSON:', err);
    }

    // Host element layout — set height in PIXELS directly so the container
    // has non-zero dimensions immediately. Using CSS variables or percentage
    // heights can resolve to 0 if the browser hasn't computed layout yet,
    // causing Leaflet's SVG renderer to initialize with undefined _bounds.
    const heightPx = parseInt(this.style.getPropertyValue('--map-height')) || 500;
    this.style.display   = 'block';
    this.style.position  = 'relative';
    this.style.height    = `${heightPx}px`;

    const loader = this.#createLoader();
    this.appendChild(loader);

    const mapDiv = document.createElement('div');
    mapDiv.style.cssText = `width:100%;height:${heightPx}px;`;
    this.insertBefore(mapDiv, loader);

    const map = new LeafletMap(mapDiv, { zoomControl: true, scrollWheelZoom: true });
    this.#map = map;

    const osmLayer = new TileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
      maxNativeZoom: 19,
      maxZoom: 22,
    });

    const satelliteLayer = new TileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        attribution:
          'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        maxNativeZoom: 19,
        maxZoom: 22,
      }
    );

    osmLayer.addTo(map);
    new Control.Layers({ '地図': osmLayer, '衛星写真': satelliteLayer }, {}, { collapsed: false }).addTo(map);

    if (bboxAttr) {
      const c = bboxAttr.split(',').map(Number);
      if (c.length === 4 && c.every((n) => !isNaN(n))) {
        map.fitBounds([[c[1], c[0]], [c[3], c[2]]], { animate: false });
      }
    }

    const signal = this.#abortController?.signal ?? new AbortController().signal;

    const showLegend = true;

    /** @type {{ config: object, leafletLayer: any }[]} */
    const renderedLayers = [];

    try {
      // Decide which layers to request — drop placeholder blocks with empty name,
      // and silently skip blocks whose tableName is no longer in the public
      // contract (e.g. obsolete typed-booth tables left over after a migration).
      // Warning per skipped layer so operators can clean up at their leisure
      // without breaking the rest of the map for visitors today.
      const layersToFetch = layers
        .filter((l) => ((l.tableName || l.name) || '').trim())
        .filter((l) => {
          const name = (l.tableName || l.name).trim();
          if (PUBLIC_LAYER_NAMES.has(name)) return true;
          console.warn(`[map-component] skipping unknown layer "${name}" — drop the matching block in the section editor`);
          return false;
        });

      if (eventId && apiBase && layersToFetch.length > 0) {
        const layerNames = layersToFetch.map((l) => (l.tableName || l.name).trim());
        // One HTTP call, one Lambda invocation, N parallel SQL queries inside.
        // Sidesteps the per-account Lambda concurrency cap entirely.
        const batchPromise = fetchPublicLayersBatch(apiBase, eventId, layerNames, { signal });

        const iconUrls = Array.from(new Set(layers.map((l) => l.iconUrl).filter(Boolean)));
        const iconPromises = iconUrls.map((url) =>
          loadIconSize(url, layers.find((l) => l.iconUrl === url)?.iconSize || 24)
            .then((size) => [url, size])
        );

        const [fcMap, iconEntries] = await Promise.all([batchPromise, Promise.all(iconPromises)]);
        const iconSizes = new Map(iconEntries);

        map.invalidateSize({ animate: false });

        // Add layers synchronously in declaration order — no awaits here, no
        // interleaving with the other map instance's fitBounds animations.
        for (const layer of layersToFetch) {
          const layerName = (layer.tableName || layer.name).trim();
          const geojson = fcMap[layerName];
          if (!geojson || !Array.isArray(geojson.features)) {
            console.warn(`[map-component] Missing layer "${layer.name}" in batch response`);
            continue;
          }

          // Skip features with invalid coordinates (malformed source data).
          geojson.features = geojson.features.filter((f) => {
            if (!f.geometry || !f.geometry.coordinates) return false;
            const [cx, cy] = _firstCoord(f.geometry.coordinates);
            return isFinite(cx) && isFinite(cy) && Math.abs(cx) <= 180 && Math.abs(cy) <= 90;
          });

          // A layer the current event simply doesn't have comes back as an empty
          // FeatureCollection (200, not 404) — series grow their table set on
          // demand. Skipping it here keeps it out of `renderedLayers`, and so out
          // of the legend, which is what lets ONE generic course/venue template
          // list every series' layer names without showing dead rows: 富士グラベル
          // has short+middle, やまなし short+long, エクストラ long. Without this a
          // shared template shows a checkbox that toggles nothing.
          if (geojson.features.length === 0) continue;

          let leafletLayer = null;
          try {
            leafletLayer = this.#renderLayerSync(map, geojson, layer, iconSizes);
            renderedLayers.push({ config: layer, leafletLayer });
          } catch (layerErr) {
            if (leafletLayer) {
              try { map.removeLayer(leafletLayer); } catch (_) {}
            }
            console.warn(`[map-component] Failed to render layer "${layer.name}":`, layerErr.message, layerErr.stack);
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[map-component]', err.message);
      }
    }

    // No bbox given → frame the map on the data we actually rendered.
    //
    // Without this the map never gets a view at all when `bbox` is absent: the
    // only fitBounds call is the bboxAttr branch above, so a blank setting left
    // Leaflet at its default world view and the venue happened to be wherever
    // it landed. Per-event templates hardcode a bbox, which is exactly what the
    // SHARED event-course / event-venue templates cannot do — one bbox can't
    // serve every edition. Fitting to the union of the rendered layers gives
    // each map the right framing for free: the course map lands on the course
    // extent (the courses are the widest layer), the venue map on the park.
    if (!bboxAttr && renderedLayers.length > 0) {
      let bounds = null;
      for (const { leafletLayer } of renderedLayers) {
        if (typeof leafletLayer?.getBounds !== 'function') continue;
        let b;
        try { b = leafletLayer.getBounds(); } catch (_) { continue; }
        // A layer whose table is empty for this event yields invalid bounds —
        // an expected outcome (series grow their table set on demand), so skip
        // it rather than poisoning the union with NaNs.
        if (!b || !b.isValid()) continue;
        // Copy rather than extend `b` directly: some Leaflet layer types return
        // their internal _bounds from getBounds(), and extend() mutates in place.
        if (bounds) bounds.extend(b)
        else bounds = new LatLngBounds(b.getSouthWest(), b.getNorthEast());
      }
      if (bounds && bounds.isValid()) {
        map.fitBounds(bounds, { padding: [24, 24], animate: false });
      }
    }

    if (showLegend && renderedLayers.length > 0) {
      this.#buildLegend(map, renderedLayers);
    }

    loader.remove();
  }

  /**
   * Render a GeoJSON FeatureCollection on the Leaflet map with per-layer styling and popups.
   *
   * @param {any}    map     Leaflet Map instance
   * @param {object} geojson GeoJSON FeatureCollection
   * @param {object} layer   Layer config object from the `layers` attribute
   */
  #renderLayerSync(map, geojson, layer, iconSizes) {
    const color       = layer.color       || '#3b82f6';
    const fillOpacity = layer.fillOpacity ?? 0.25;
    const labelField  = layer.labelField  || 'label';
    const showLabel   = layer.showLabel   ?? false;
    const layerName   = layer.name        || '';
    const iconUrl     = layer.iconUrl     || null;
    const iconSize    = layer.iconSize    || 24;
    const dashArray   = layer.dashArray   || null;
    const offset      = layer.offset     || 0;
    // Optional per-feature colouring: { field: 'booth_type', map: { brand: '#…', food: '#…', … } }.
    // The booth_spots layer uses this to render type-coloured polygons in a
    // single layer; falls back to `color` when the field value is missing.
    const colorByField = layer.colorByField || null;
    const [iconW, iconH] = iconUrl ? (iconSizes.get(iconUrl) || [iconSize, iconSize]) : [iconSize, iconSize];

    const styleFor = (feature) => {
      let c = color;
      if (colorByField && colorByField.field && colorByField.map) {
        const key = feature?.properties?.[colorByField.field];
        if (key != null && colorByField.map[key]) c = colorByField.map[key];
      }
      return { color: c, weight: 3, fillColor: c, fillOpacity, dashArray, offset };
    };

    const leafletLayer = new LeafletGeoJSON(geojson, {
      style: styleFor,

      pointToLayer: (_feature, latlng) => {
        if (iconUrl) {
          return new Marker(latlng, {
            icon: new Icon({
              iconUrl,
              iconSize:    [iconW, iconH],
              iconAnchor:  [iconW / 2, iconH / 2],
              popupAnchor: [0, -(iconH / 2)],
            }),
          });
        }
        return new CircleMarker(latlng, {
          radius: 7, color, weight: 2, fillColor: color, fillOpacity: 0.85,
        });
      },

      onEachFeature: (feature, featureLayer) => {
        const props = feature.properties || {};

        // Custom popup for booth_spots: mirror the storefront's listing card
        // (logo + name + surface/物販/試乗車 badges) wrapped in a link to the
        // dynamic detail page. No long description. Organizer booths render
        // the same card without the link wrapper.
        if ((layer.tableName || layer.name) === 'booth_spots') {
          featureLayer.bindPopup(buildBoothSpotPopup(props));
          return;
        }

        const title = props[labelField] || layerName;
        const rows  = Object.entries(props)
          .filter(([k, v]) => k !== 'fid' && k !== labelField && v != null && v !== '')
          .map(
            ([k, v]) =>
              `<tr>` +
              `<th style="text-align:left;padding-right:8px;white-space:nowrap;font-weight:normal;color:#666;">${k}</th>` +
              `<td>${formatValue(v)}</td>` +
              `</tr>`
          )
          .join('');

        featureLayer.bindPopup(
          `<strong style="display:block;margin-bottom:4px;">${title}</strong>` +
          (rows ? `<table style="font-size:12px;border-collapse:collapse;">${rows}</table>` : '')
        );

      },
    });

    try {
      leafletLayer.addTo(map);
    } catch (err) {
      try { map.removeLayer(leafletLayer); } catch (_) {}
      throw err;
    }

    // Bind permanent tooltips AFTER addTo so each sublayer's SVG _path exists.
    // Wrap in try/catch per feature as a safety net for edge cases.
    // booth_spots is special-cased: the layer's own showLabel stays false
    // (booths are identified by logo + popup), but organizer booths get a
    // permanent label so 本部 / 受付 / 救護 etc. are obvious on the map.
    const isBoothSpots = (layer.tableName || layer.name) === 'booth_spots';
    if (showLabel || isBoothSpots) {
      leafletLayer.eachLayer((featureLayer) => {
        try {
          const props = featureLayer.feature?.properties || {};
          if (isBoothSpots && props.booth_type !== 'organizer') return;
          const title = isBoothSpots
            ? (props.label || props.exhibitor_name || layerName)
            : (props[labelField] || layerName);
          if (!title || !featureLayer.getElement?.()) return;
          const geomType = featureLayer.feature?.geometry?.type || '';
          const isPoint  = geomType === 'Point' || geomType === 'MultiPoint';
          featureLayer.bindTooltip(title, {
            permanent:  true,
            direction:  isPoint ? 'top' : 'center',
            className:  'map-component-label',
          });
        } catch (_) { /* skip tooltip for features without valid _path */ }
      });
    }

    return leafletLayer;
  }

  /**
   * Build an interactive legend as a Leaflet control.
   * Groups layers by their `group` config property when present.
   *
   * @param {any} map  Leaflet Map instance
   * @param {{ config: object, leafletLayer: any }[]} renderedLayers
   */
  #buildLegend(map, renderedLayers) {
    const legend = new Control({ position: 'bottomright' });

    legend.onAdd = (mapInstance) => {
      const container = DomUtil.create('div', 'map-component-legend');
      container.style.maxHeight = `${mapInstance.getSize().y - 80}px`;
      DomEvent.disableClickPropagation(container);
      DomEvent.disableScrollPropagation(container);

      // Organise layers into groups and standalone items (preserving order)
      /** @type {{ type: 'group', name: string, items: typeof renderedLayers } | { type: 'layer', item: typeof renderedLayers[0] }}[] */
      const sections = [];
      const groupMap = new Map();

      for (const entry of renderedLayers) {
        const groupName = entry.config.group || null;
        if (groupName) {
          if (!groupMap.has(groupName)) {
            const group = { type: 'group', name: groupName, items: [] };
            groupMap.set(groupName, group);
            sections.push(group);
          }
          groupMap.get(groupName).items.push(entry);
        } else {
          sections.push({ type: 'layer', item: entry });
        }
      }

      for (const section of sections) {
        if (section.type === 'group') {
          const groupDiv = document.createElement('div');
          groupDiv.className = 'map-legend-group';

          const header = document.createElement('label');
          header.className = 'map-legend-group-header';
          const groupCb = document.createElement('input');
          groupCb.type = 'checkbox';
          groupCb.checked = true;
          header.appendChild(groupCb);
          header.appendChild(document.createTextNode(` ${section.name}`));
          groupDiv.appendChild(header);

          const childCbs = [];
          for (const entry of section.items) {
            const row = this.#createLegendRow(map, entry);
            groupDiv.appendChild(row.element);
            childCbs.push(row.checkbox);
          }

          // Group toggle controls all children
          groupCb.addEventListener('change', () => {
            for (const cb of childCbs) {
              if (cb.checked !== groupCb.checked) {
                cb.checked = groupCb.checked;
                cb.dispatchEvent(new Event('change'));
              }
            }
          });

          container.appendChild(groupDiv);
        } else {
          const row = this.#createLegendRow(map, section.item);
          container.appendChild(row.element);
        }
      }

      return container;
    };

    legend.addTo(map);
  }

  /**
   * Create a single legend row with a colored swatch, name, and visibility toggle.
   * @returns {{ element: HTMLElement, checkbox: HTMLInputElement }}
   */
  #createLegendRow(map, { config, leafletLayer }) {
    const row = document.createElement('label');
    row.className = 'map-legend-row';

    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = true;
    cb.addEventListener('change', () => {
      try {
        if (cb.checked) {
          map.addLayer(leafletLayer);
        } else {
          map.removeLayer(leafletLayer);
        }
      } catch (_) { /* layer may not have rendered properly */ }
    });

    const swatch = document.createElement('span');
    swatch.className = 'map-legend-swatch';
    const color = config.color || '#3b82f6';
    const dashArray = config.dashArray || null;

    // Determine swatch appearance based on geometry hints
    if (config.iconUrl) {
      swatch.style.cssText = `background:url(${config.iconUrl}) center/contain no-repeat;border:none;`;
    } else if (dashArray) {
      swatch.style.cssText = `background:transparent;border:none;border-top:3px dashed ${color};border-radius:0;align-self:center;height:0;`;
    } else {
      swatch.style.cssText = `background:${color};opacity:0.8;`;
    }

    row.appendChild(cb);
    row.appendChild(swatch);
    row.appendChild(document.createTextNode(` ${config.name || ''}`));

    return { element: row, checkbox: cb };
  }

  #createLoader() {
    const el = document.createElement('div');
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText =
      'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(248,248,248,0.85);z-index:9999;font-size:14px;color:#666;pointer-events:none;';
    el.textContent = '地図を読み込み中…';
    return el;
  }
}

customElements.define('map-component', MapComponent);
